/* respell-app.js — the page of /pronunciation-respelling: English read aloud in
 * the reader's own alphabet ("английские слова русскими буквами").
 *
 * Who does what. respell-core.js decides WHAT is on the page and is unit-tested
 * in node (tests/js/respell-core.test.mjs); lang-script.js turns IPA into
 * letters; lang-stress.js reads a word (dictionary, heteronyms by context, the
 * stress of a word the dictionary lacks); lang-connected.js knows how function
 * words weaken in running speech; lang-gloss.js translates words both ways.
 * This file wires them to the DOM and the two voices; a photo of a page comes
 * in through the shared lane (public/js/lang/photo-text.js).
 *
 * Config arrives from the blade as window.__RS (strings, scripts, lanes); no
 * user-visible text lives here.
 *
 * Plain IIFE, no build step. Needs RespellCore, LangIPA, LangScript, LangStress;
 * LangConnected, LangGloss, TTSHelper, PhotoText are optional and degrade.
 */
(function () {
  'use strict';

  var CFG = window.__RS || {};
  var I = CFG.i18n || {};
  var SLUG = CFG.slug || 'pronunciation-respelling';
  var LOC = CFG.loc || 'en';
  var Core = window.RespellCore;
  var MAX_CHARS = 20000;
  var HD_FIRST = 350, HD_MAX = 1500;
  var NO_INLINE_GLOSS = { a: 1, an: 1, the: 1, am: 1, is: 1, are: 1 };
  var SPEEDS = [1, 0.75, 0.5];

  /* ── small helpers ─────────────────────────────────────────────────────── */
  function $(id) { return document.getElementById(id); }
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function fmt(s, o) {
    return String(s == null ? '' : s).replace(/:([a-z_]+)/g, function (m, k) {
      return (o && o[k] != null) ? String(o[k]) : m;
    });
  }
  function t(k, o) { return fmt(I[k] != null ? I[k] : '', o); }
  function noop() {}
  function uniq(list) {
    var seen = Object.create(null), out = [];
    for (var i = 0; i < list.length; i++) {
      var k = String(list[i]).toLowerCase();
      if (!seen[k]) { seen[k] = 1; out.push(list[i]); }
    }
    return out;
  }
  function ae(ev, d) { try { if (window.__ae) window.__ae(ev, d); } catch (e) { /* telemetry never breaks the page */ } }
  var fired = Object.create(null);
  function feature(f, extra, every) {
    if (!every && fired[f]) return;
    fired[f] = 1;
    var d = { tool_slug: SLUG, f: f };
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) d[k] = extra[k];
    ae('feature_use', d);
  }
  function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (e) { /* ignore */ } }
  function announce(msg) { try { if (window.__announce) window.__announce(msg); } catch (e) { /* ignore */ } }

  /* Settings: the shared store (window.__toolSettings) is defined by the layout
   * AFTER the content, so every read happens at boot, never at parse time. */
  function TS() { return window.__toolSettings || null; }
  function sload(k, d) {
    var s = TS(); if (!s) return d;
    try { var v = s.load(SLUG, k, d); return v == null ? d : v; } catch (e) { return d; }
  }
  function ssave(k, v) { var s = TS(); if (!s) return; try { s.save(SLUG, k, v); } catch (e) { /* storage full / blocked */ } }

  function accent() { return (window.__enVariant && window.__enVariant.isUS()) ? 'us' : 'gb'; }
  function ttsLang() { return accent() === 'us' ? 'en-US' : 'en-GB'; }

  var SCRIPT_DIR = { ar: 'rtl' };
  var SCRIPT_LANG = { ru: 'ru', uk: 'uk', en: 'en', es: 'es', pt: 'pt', de: 'de', fr: 'fr', id: 'id', ko: 'ko', ar: 'ar' };

  /* ── state ─────────────────────────────────────────────────────────────── */
  var S = {
    script: CFG.script || 'ru',
    view: 'over',
    speed: 1,
    voice: 'device',
    opt: { stress: true, long: true, weak: true, gloss: !!CFG.gloss, en: true }
  };
  var M = { lines: [], fixed: [], truncated: false, words: 0, native: 0, run: 0, text: '' };
  var picks = Object.create(null);        // 'word#n' → heteronym reading chosen by the person
  var nativePick = Object.create(null);   // native word (key) → English word chosen for it
  var userEdited = false;

  var E = {};                             // elements, filled at boot

  /* ── engines ───────────────────────────────────────────────────────────── */
  var enginesP = null;
  function engines() {
    if (enginesP) return enginesP;
    if (!Core || !window.LangIPA || !window.LangScript || !window.LangStress) return Promise.reject(new Error('engine_missing'));
    enginesP = Promise.resolve(window.LangStress.load()).catch(function (e) { enginesP = null; throw e; });
    return enginesP;
  }
  var glossP = null, glossOK = false;
  function glossReady() {
    if (!CFG.gloss || !window.LangGloss) return Promise.resolve(false);
    if (!glossP) {
      glossP = Promise.resolve(window.LangGloss.load(LOC)).then(function (m) {
        glossOK = !!m;
        if (!m) glossP = null;     // a network failure is not remembered
        return glossOK;
      });
    }
    return glossP;
  }
  function glossShown() { return glossOK && S.opt.gloss && CFG.gloss; }

  /* ── analysis ──────────────────────────────────────────────────────────── */
  function isWordTok(tok) { return tok.kind === 'en' || tok.kind === 'native' || tok.kind === 'num'; }
  function breakBetween(line, a, b) {
    for (var i = a + 1; i < b; i++) if (line[i].kind === 'punct' && /[.!?;:…]/.test(line[i].t)) return true;
    return false;
  }
  function nativeKey(w) { return window.LangGloss && window.LangGloss.revKey ? window.LangGloss.revKey(w, LOC) : String(w).toLowerCase(); }
  function chosenEnglish(tok) {
    if (!tok.cands || !tok.cands.length) return null;
    var p = nativePick[nativeKey(tok.t)];
    return p && tok.cands.indexOf(p) >= 0 ? p : tok.cands[0];
  }

  var analyseTimer = 0;
  function schedule(ms) { clearTimeout(analyseTimer); analyseTimer = setTimeout(analyse, ms == null ? 220 : ms); }

  function analyse() {
    var my = ++M.run;
    var raw = E.input.value;
    var truncated = raw.length > MAX_CHARS;
    var fx = Core.fixHomoglyphs(truncated ? raw.slice(0, MAX_CHARS) : raw);
    var lines = Core.splitLines(fx.text);
    var enWords = [], natWords = [], occ = Object.create(null);
    lines.forEach(function (line, li) {
      line.forEach(function (tok, ti) {
        tok.li = li; tok.ti = ti;
        if (tok.kind === 'en') {
          var low = tok.t.toLowerCase();
          occ[low] = (occ[low] || 0) + 1;
          tok.key = low + '#' + occ[low];
          enWords.push(tok.t);
        } else if (tok.kind === 'native') natWords.push(tok.t);
      });
    });
    stopAll();
    return engines().then(function () {
      if (my !== M.run) return null;
      var list = uniq(enWords);
      return Promise.all([
        list.length ? Promise.resolve(window.LangIPA.phrase(list.join(' '))).catch(noop) : null,
        glossReady().then(function (ok) {
          return ok && list.length ? Promise.resolve(window.LangGloss.ensure(list)).catch(noop) : null;
        })
      ]);
    }).then(function () {
      if (my !== M.run) return null;
      return resolveNative(lines, my);
    }).then(function () {
      if (my !== M.run) return;
      M.lines = lines; M.fixed = fx.fixed; M.truncated = truncated; M.text = fx.text;
      M.words = 0; M.native = 0;
      lines.forEach(function (line) {
        analyseLine(line);
        line.forEach(function (tok) { if (tok.kind === 'en') M.words++; else if (tok.kind === 'native') M.native++; });
      });
      render();
      paintHomoglyphs();
      paintOffer();
      paintCount();
      if (userEdited && M.words && !fired.respell) {
        feature('respell', { script: S.script, words: M.words, lines: lines.length });
        try { if (window.__aeToolComplete) window.__aeToolComplete({ script: S.script, words: M.words }); } catch (e) { /* ignore */ }
      }
      if (mtOn) translateLines();
    }).catch(function () {
      if (my !== M.run) return;
      E.out.textContent = '';
      E.out.appendChild(el('p', 'rs-empty', t('engine_error')));
    });
  }

  /* Words typed in the reader's own language (школа, 학교, مدرسة, escuela):
   * the person does not know the English word yet. The reverse dictionary
   * finds it; in a Latin-script language a word counts as the reader's own only
   * when English does not know it AND the reverse dictionary does. */
  function resolveNative(lines, my) {
    if (!CFG.gloss || !window.LangGloss || !window.LangGloss.reverse) return Promise.resolve();
    var latinPage = CFG.nativeScript === 'Latn';
    var ask = [];
    lines.forEach(function (line) {
      line.forEach(function (tok) {
        if (tok.kind === 'native') ask.push(tok);
        else if (latinPage && tok.kind === 'en' && tok.t.length > 2) {
          var hit = window.LangIPA.toIPASync ? window.LangIPA.toIPASync(tok.t, {}) : null;
          if (!hit || hit.source !== 'cmudict') ask.push(tok);
        }
      });
    });
    if (!ask.length) return Promise.resolve();
    return window.LangGloss.reverse(uniq(ask.map(function (x) { return x.t; })), LOC).then(function (map) {
      if (my !== M.run) return null;
      var want = [];
      ask.forEach(function (tok) {
        var hits = map[tok.t];
        if (!hits || !hits.length) { if (tok.kind === 'native') tok.cands = []; return; }
        tok.kind = 'native';
        tok.cands = Core.preferVariant(hits, accent() === 'us' ? 'us' : 'uk');
        want = want.concat(tok.cands);
      });
      return want.length ? Promise.resolve(window.LangIPA.phrase(uniq(want).join(' '))).catch(noop) : null;
    }).catch(noop);
  }

  function analyseLine(line) {
    var acc = accent();
    var idx = [];
    for (var i = 0; i < line.length; i++) if (line[i].kind === 'en') idx.push(i);
    for (var k = 0; k < idx.length; k++) {
      var tok = line[idx[k]];
      tok.prev = k > 0 && !breakBetween(line, idx[k - 1], idx[k]) ? line[idx[k - 1]].t : null;
      tok.next = k + 1 < idx.length && !breakBetween(line, idx[k], idx[k + 1]) ? line[idx[k + 1]].t : null;
      tok.a = window.LangStress.analyzeWord(tok.t, tok.prev, tok.next, acc);
      var pk = picks[tok.key];
      if (pk != null && tok.a.variants && tok.a.variants[pk]) {
        window.LangStress.pick(tok.a, pk);
        tok.a.ipaSyllables = tok.a.variants[pk].ipaSyllables;
      }
    }
    /* Connected speech reads the whole line: «to» before a vowel, a stranded
     * preposition, the last word of a sentence — position decides the form. */
    var lc = line.map(function (x) {
      if (x.kind === 'space') return { token: x.t, space: true };
      if (x.kind === 'punct') return { token: x.t, punct: true };
      return { token: x.kind === 'native' ? (chosenEnglish(x) || x.t) : x.t };
    });
    var cls = null;
    try { cls = window.LangConnected ? window.LangConnected.classify(lc) : null; } catch (e) { cls = null; }
    for (var j = 0; j < line.length; j++) {
      var tk = line[j];
      if (tk.kind === 'en' && tk.a) {
        tk.wf = null;
        try { tk.wf = cls ? window.LangConnected.weakForm(tk.t, j, lc, acc, cls) : null; } catch (e) { tk.wf = null; }
      }
    }
    respellLine(line);
  }

  /* The reading shown for a word HERE: in running speech a function word takes
   * its weak form («a cup of tea» — «э кап эв ти:»); with the switch off every
   * word keeps its dictionary form, except the one rule every course teaches —
   * «the» before a vowel sound is «зи». */
  function formOf(tok) {
    var a = tok.a, wf = tok.wf;
    if (wf && wf.ipa && S.opt.weak) return { ipa: wf.ipa, syllables: null, weak: !!wf.weak };
    if (wf && wf.ipa && !S.opt.weak && tok.t.toLowerCase() === 'the' && wf.why === 'before a vowel') return { ipa: wf.ipa, syllables: null, weak: false };
    return { ipa: a.ipa, syllables: a.ipaSyllables, weak: false };
  }
  function respellIPA(ipa, syllables, token, script, acc) {
    var r = window.LangScript.respellResult({ ipa: ipa, syllables: syllables }, script || S.script,
      { stress: S.opt.stress, long: S.opt.long, accent: acc || accent() });
    if (token && window.LangScript.stressStyleOf && window.LangScript.stressStyleOf(script || S.script) === 'acute') {
      var cap = window.LangScript.capitalize(r.text, token);
      if (cap !== r.text) { r.text = cap; r.cap = true; }
    }
    return r;
  }
  function respellLine(line) {
    for (var i = 0; i < line.length; i++) {
      var tok = line[i];
      if (tok.kind === 'en' && tok.a) {
        tok.form = formOf(tok);
        tok.r = tok.a.ipa ? respellIPA(tok.form.ipa, tok.form.syllables, tok.t) : null;
      } else if (tok.kind === 'native') {
        var en = chosenEnglish(tok);
        tok.na = en ? window.LangStress.analyzeWord(en, null, null, accent()) : null;
        tok.r = tok.na && tok.na.ipa ? respellIPA(tok.na.ipa, tok.na.ipaSyllables, null) : null;
      }
    }
  }
  function respellAll() { M.lines.forEach(respellLine); render(); }
  function reanalyseAll() { M.lines.forEach(analyseLine); render(); }

  /* ── rendering ─────────────────────────────────────────────────────────── */
  function capFirst(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  /* A respelling as letters: the stressed vowel of a longer word stands out,
   * a letter that only approximates an English sound gets a dotted line (and
   * names the sound, so the legend can explain it). */
  function paintRespelling(r, holder) {
    var segs = (r && r.segs) || [];
    if (!segs.length || (segs.length === 1 && !segs[0].u)) { holder.appendChild(document.createTextNode(r ? r.text : '')); return; }
    var nuclei = 0;
    for (var i = 0; i < segs.length; i++) if (segs[i].vowel && segs[i].o) nuclei++;
    var first = true;
    for (var j = 0; j < segs.length; j++) {
      var s = segs[j], o = s.o;
      if (!o) continue;
      if (first && r.cap) o = capFirst(o);
      first = false;
      if (s.approx) {
        var sp = el('span', 'ap', o);
        if (s.sound) sp.setAttribute('data-s', s.sound);
        holder.appendChild(sp);
      } else if (s.vowel && s.st === 1 && nuclei > 1 && S.opt.stress) {
        holder.appendChild(el('b', 'st', o));
      } else holder.appendChild(document.createTextNode(o));
    }
  }

  function glossFor(tok, single) {
    if (!glossShown() || tok.kind !== 'en' || !window.LangGloss) return null;
    if (!single && NO_INLINE_GLOSS[tok.t.toLowerCase()]) return null;
    var hint = (tok.a && tok.a.isHeteronym && (tok.a.role === 'noun' || tok.a.role === 'verb'))
      ? tok.a.role : window.LangGloss.posHint(tok.prev, tok.next);
    var g = window.LangGloss.lookup(tok.t, hint);
    if (!g) return null;
    return single ? window.LangGloss.full(g) : g.short;
  }

  function groupChips(line) {
    var out = [], lead = '';
    for (var i = 0; i < line.length; i++) {
      var tok = line[i];
      if (tok.kind === 'space') { if (lead) { out.push({ text: lead }); lead = ''; } continue; }
      if (tok.kind === 'punct') {
        var prev = line[i - 1], last = out[out.length - 1];
        if (prev && prev.kind !== 'space' && last && last.tok) last.trail += tok.t;
        else lead += tok.t;
        continue;
      }
      out.push({ tok: tok, lead: lead, trail: '' });
      lead = '';
    }
    if (lead) out.push({ text: lead });
    return out;
  }
  function lineHasWords(line) { for (var i = 0; i < line.length; i++) if (isWordTok(line[i])) return true; return false; }
  function lineHasContent(line) { for (var i = 0; i < line.length; i++) if (line[i].kind !== 'space') return true; return false; }
  function tokRef(tok) { return tok.li + ':' + tok.ti; }
  function tokOf(ref) {
    var p = String(ref || '').split(':');
    var line = M.lines[+p[0]];
    return line ? line[+p[1]] : null;
  }
  function singleWord() { return M.words + M.native === 1; }

  function chipEl(c) {
    var tok = c.tok, single = singleWord();
    var w = el('span', 'rs-w');
    w.tabIndex = 0;
    w.setAttribute('role', 'button');
    w.setAttribute('data-k', tokRef(tok));
    w.appendChild(el('span', 'rs-e', c.lead + tok.t + c.trail));
    var r = el('span', 'rs-r');
    r.lang = SCRIPT_LANG[S.script] || 'und';
    r.dir = SCRIPT_DIR[S.script] || 'ltr';
    if (c.lead) r.appendChild(document.createTextNode(c.lead));
    if (tok.kind === 'num') r.appendChild(document.createTextNode(tok.t));
    else if (tok.r) paintRespelling(tok.r, r);
    else r.appendChild(document.createTextNode('?'));
    if (c.trail) r.appendChild(document.createTextNode(c.trail));
    w.appendChild(r);
    if (tok.kind === 'en') {
      if (tok.form && tok.form.weak) w.classList.add('weak');
      if (tok.a && tok.a.approximate) w.classList.add('oov');
      if (tok.a && tok.a.isHeteronym) w.classList.add('het');
      var g = glossFor(tok, single);
      if (g) { var ge = el('span', 'rs-g', g); ge.lang = LOC; ge.dir = 'auto'; w.appendChild(ge); }
    } else if (tok.kind === 'native') {
      w.classList.add('nat');
      var en = chosenEnglish(tok);
      if (en) { var ne = el('span', 'rs-g rs-g-en', en); ne.lang = 'en'; w.appendChild(ne); }
      else w.classList.add('miss');
    } else w.classList.add('num');
    if (single) w.classList.add('one');
    return w;
  }

  function renderOver() {
    var box = el('div', 'rs-over');
    M.lines.forEach(function (line, li) {
      if (!lineHasContent(line)) { if (li && li < M.lines.length - 1) box.appendChild(el('div', 'rs-gap')); return; }
      var row = el('div', 'rs-line');
      row.setAttribute('data-li', li);
      groupChips(line).forEach(function (c) { row.appendChild(c.tok ? chipEl(c) : el('span', 'rs-p', c.text)); });
      box.appendChild(row);
      var tr = lineTranslation(li, line);
      if (tr) box.appendChild(tr);
    });
    E.out.appendChild(box);
  }

  function lineSource(line) {
    var s = '';
    for (var i = 0; i < line.length; i++) s += line[i].kind === 'space' ? ' ' : line[i].t;
    return s.replace(/\s+/g, ' ').trim();
  }

  function renderLines() {
    var box = el('div', 'rs-rows' + (S.opt.en ? '' : ' no-en'));
    M.lines.forEach(function (line, li) {
      if (!lineHasContent(line)) { if (li && li < M.lines.length - 1) box.appendChild(el('div', 'rs-gap')); return; }
      var row = el('div', 'rs-row');
      row.setAttribute('data-li', li);
      if (lineHasWords(line)) {
        var pb = el('button', 'rs-row-play');
        pb.type = 'button';
        pb.setAttribute('data-li', li);
        pb.setAttribute('aria-label', t('play_line'));
        pb.title = t('play_line');
        row.appendChild(pb);
      }
      var body = el('div', 'rs-row-body');
      if (S.opt.en) body.appendChild(el('div', 'rs-row-e', lineSource(line)));
      var r = el('div', 'rs-row-r');
      r.lang = SCRIPT_LANG[S.script] || 'und';
      r.dir = SCRIPT_DIR[S.script] || 'ltr';
      var pendingSpace = false;
      for (var i = 0; i < line.length; i++) {
        var tok = line[i];
        if (tok.kind === 'space') { pendingSpace = true; continue; }
        if (pendingSpace && r.childNodes.length) r.appendChild(document.createTextNode(' '));
        pendingSpace = false;
        if (tok.kind === 'punct' || tok.kind === 'num') { r.appendChild(document.createTextNode(tok.t)); continue; }
        var s = el('span', 'rs-lw' + (tok.form && tok.form.weak ? ' weak' : '') + (tok.kind === 'native' ? ' nat' : ''));
        s.setAttribute('data-k', tokRef(tok));
        if (tok.r) paintRespelling(tok.r, s); else s.textContent = tok.t;
        r.appendChild(s);
      }
      body.appendChild(r);
      var tr = lineTranslation(li, line);
      if (tr) body.appendChild(tr);
      row.appendChild(body);
      box.appendChild(row);
    });
    E.out.appendChild(box);
  }

  function vocabRows() {
    var seen = Object.create(null), rows = [];
    M.lines.forEach(function (line) {
      line.forEach(function (tok) {
        if (tok.kind !== 'en' && tok.kind !== 'native') return;
        var word = tok.kind === 'native' ? chosenEnglish(tok) : tok.t;
        if (!word) return;
        var key = word.toLowerCase().replace(/’/g, "'");
        if (seen[key]) return;
        seen[key] = 1;
        var a = tok.kind === 'native' ? tok.na : tok.a;
        if (!a) return;
        var r = tok.kind === 'native' ? tok.r : respellIPA(a.ipa, a.ipaSyllables, null);
        var gloss = '';
        if (tok.kind === 'native') gloss = tok.t;
        else if (glossShown()) { var g = window.LangGloss.lookup(tok.t, null); gloss = g ? window.LangGloss.full(g) : ''; }
        rows.push({ tok: tok, word: word, r: r, ipa: a.ipa ? '/' + a.ipa + '/' : '', gloss: gloss });
      });
    });
    return rows;
  }
  function renderVocab() {
    var rows = vocabRows();
    var wrap = el('div', 'tc-tablewrap');
    var table = el('table', 'tc-table rs-vocab');
    var thead = el('thead'), hr = el('tr');
    var withGloss = glossShown() || rows.some(function (r) { return r.tok.kind === 'native'; });
    ['', t('col_word'), t('col_respell'), t('col_ipa')].concat(withGloss ? [t('col_gloss')] : []).forEach(function (h) { hr.appendChild(el('th', '', h)); });
    thead.appendChild(hr); table.appendChild(thead);
    var tb = el('tbody');
    rows.forEach(function (row) {
      var tr = el('tr');
      tr.setAttribute('data-k', tokRef(row.tok));
      var c0 = el('td', 'v-play');
      var b = el('button', 'rs-mini-play');
      b.type = 'button';
      b.setAttribute('data-say', row.word);
      b.setAttribute('aria-label', t('listen') + ': ' + row.word);
      c0.appendChild(b);
      tr.appendChild(c0);
      var cw = el('td', 'v-w', row.word);
      cw.lang = 'en';
      cw.tabIndex = 0; cw.setAttribute('role', 'button');
      tr.appendChild(cw);
      var cr = el('td', 'v-r');
      cr.lang = SCRIPT_LANG[S.script] || 'und'; cr.dir = SCRIPT_DIR[S.script] || 'ltr';
      if (row.r) paintRespelling(row.r, cr);
      tr.appendChild(cr);
      tr.appendChild(el('td', 'v-ipa', row.ipa));
      if (withGloss) { var cg = el('td', 'v-g', row.gloss || '—'); cg.lang = LOC; cg.dir = 'auto'; tr.appendChild(cg); }
      tb.appendChild(tr);
    });
    table.appendChild(tb);
    wrap.appendChild(table);
    E.out.appendChild(wrap);
    lastTable = Core.tsv(rows.map(function (r) {
      return [r.word, r.r ? r.r.text : '', r.ipa].concat(withGloss ? [r.gloss] : []);
    }));
  }
  var lastTable = '';

  function render() {
    closeCard();
    E.out.textContent = '';
    E.out.setAttribute('data-view', S.view);
    E.out.classList.toggle('rs-rtl', SCRIPT_DIR[S.script] === 'rtl');
    if (!M.words && !M.native) {
      E.out.appendChild(el('p', 'rs-empty', E.input.value.trim() ? t('no_english') : t('empty_hint')));
      renderLegend();
      paintButtons();
      return;
    }
    if (S.view === 'lines') renderLines();
    else if (S.view === 'vocab') renderVocab();
    else renderOver();
    if (M.truncated) E.out.appendChild(el('p', 'tc-note', t('truncated', { n: MAX_CHARS.toLocaleString(LOC) })));
    renderLegend();
    paintButtons();
  }

  /* The sounds this text needs that the chosen alphabet has no letter for —
   * each once, with the letters that stand in for it here. */
  function renderLegend() {
    var box = E.legend;
    box.textContent = '';
    var found = Object.create(null), order = [];
    M.lines.forEach(function (line) {
      line.forEach(function (tok) {
        var r = tok.r;
        if (!r) return;
        var segs = r.segs || [];
        if (segs.length === 1 && !segs[0].u) {
          (r.sounds || []).forEach(function (id) { if (found[id] == null) { found[id] = ''; order.push(id); } });
          return;
        }
        segs.forEach(function (s) {
          if (!s.approx || !s.sound) return;
          if (found[s.sound] == null) { found[s.sound] = s.o; order.push(s.sound); }
        });
      });
    });
    var tips = CFG.tips || {};
    order = order.filter(function (id) { return tips[id]; });
    if (!order.length) { box.hidden = true; return; }
    box.hidden = false;
    var head = el('div', 'rs-legend-h');
    head.appendChild(el('span', 'rs-legend-t', t('legend_title')));
    head.appendChild(el('span', 'rs-legend-s', t('legend_hint')));
    box.appendChild(head);
    var chips = el('div', 'rs-legend-chips');
    order.forEach(function (id) {
      var b = el('button', 'rs-snd');
      b.type = 'button';
      b.setAttribute('data-s', id);
      b.appendChild(el('b', '', tips[id].ipa));
      if (found[id]) { var lt = el('span', 'rs-snd-l', found[id].replace(/\u0301/g, '')); lt.lang = SCRIPT_LANG[S.script] || 'und'; b.appendChild(lt); }
      b.appendChild(el('span', 'rs-snd-ex', tips[id].ex));
      chips.appendChild(b);
    });
    box.appendChild(chips);
    if (window.LangScript.accuracyFor && window.LangScript.accuracyFor(S.script) === 'rough') {
      box.appendChild(el('p', 'tc-note', t('rough_note')));
    }
  }

  function paintButtons() {
    var has = M.words + M.native > 0;
    [E.play, E.copy, E.share, E.print].forEach(function (b) { if (b) b.disabled = !has; });
    if (E.mt) E.mt.disabled = !has || S.view === 'vocab';
    if (E.copy && !E.copy.getAttribute('data-flash')) E.copy.querySelector('span').textContent = S.view === 'vocab' ? t('copy_table') : t('copy');
  }

  function paintCount() {
    var n = E.input.value.length;
    E.count.textContent = n ? n.toLocaleString(LOC) : '';
    E.count.classList.toggle('over', n > MAX_CHARS);
    E.clear.hidden = !n;
  }

  /* Cyrillic look-alikes typed inside English words («сat» with a Russian с):
   * read as Latin, and said so — silently fixing the text would be the page
   * editing the person's words behind their back. */
  function paintHomoglyphs() {
    var box = E.homo;
    if (!M.fixed.length) { box.hidden = true; return; }
    box.hidden = false;
    box.querySelector('.rs-homo-t').textContent = t('homoglyph_note', {
      n: M.fixed.length,
      list: M.fixed.slice(0, 3).map(function (p) { return p[1]; }).join(', ') + (M.fixed.length > 3 ? '…' : '')
    });
    feature('homoglyph', { n: M.fixed.length });
  }
  function fixHomoglyphsInField() {
    var fx = Core.fixHomoglyphs(E.input.value);
    replaceField(fx.text);
    feature('homoglyph_fix');
  }
  /* Replace the field's text keeping the browser's undo: execCommand is
   * deprecated but is still the only way that does; the assignment is the
   * fallback where it is gone. */
  function replaceField(text) {
    E.input.focus();
    var ok = false;
    try { E.input.select(); ok = document.execCommand && document.execCommand('insertText', false, text); } catch (e) { ok = false; }
    if (!ok || E.input.value !== text) E.input.value = text;
    E.input.setSelectionRange(text.length, text.length);
    onInput();
  }

  /* ── the native-sentence offer: «похоже, это по-русски — перевести?» ──── */
  function paintOffer() {
    var box = E.offer;
    var total = M.words + M.native;
    var show = CFG.gloss && M.native >= 2 && M.native / Math.max(1, total) >= 0.5;
    if (!show) { if (!offerBusy) box.hidden = true; return; }
    box.hidden = false;
    if (!offerBusy && !offerResult) {
      box.querySelector('.rs-offer-t').textContent = t('native_offer');
      E.offerGo.hidden = false; E.offerUse.hidden = true;
    }
  }
  var offerBusy = false, offerResult = '';
  function offerTranslate() {
    offerBusy = true; offerResult = '';
    E.offerGo.disabled = true;
    E.offer.querySelector('.rs-offer-t').textContent = t('mt_working');
    feature('native_mt');
    var src = E.input.value.trim();
    loadTranslator().then(function (engine) {
      var lines = src.split('\n').map(function (l) { return l.trim(); });
      var outL = [];
      return lines.reduce(function (p, l) {
        return p.then(function () {
          if (!l) { outL.push(''); return null; }
          return engine.translateEntryText(l, LOC, 'en', null, mtOpts).then(function (r) {
            if (!r || r.reason || !r.text) throw new Error((r && r.reason) || 'empty');
            outL.push(r.text);
          });
        });
      }, Promise.resolve()).then(function () { return outL.join('\n'); });
    }).then(function (text) {
      offerBusy = false; offerResult = text;
      E.offer.querySelector('.rs-offer-t').textContent = text;
      E.offerGo.hidden = true; E.offerUse.hidden = false; E.offerGo.disabled = false;
    }).catch(function () {
      offerBusy = false;
      E.offer.querySelector('.rs-offer-t').textContent = t('mt_failed');
      E.offerGo.disabled = false;
    });
  }
  function offerApply() {
    if (!offerResult) return;
    var text = offerResult;
    offerResult = '';
    E.offer.hidden = true;
    replaceField(text);
    feature('native_mt_use');
  }

  /* ── translation of lines (the three-line handout: text · reading · meaning) */
  var mtOn = false, mtRun = 0, mtPending = false, mtError = '';
  var mtCache = new Map();
  var mtOpts = {};
  var translatorP = null;
  function loadTranslator() {
    if (window.TranslatorEngine) return Promise.resolve(window.TranslatorEngine);
    if (translatorP) return translatorP;
    translatorP = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = CFG.translator || window.__jsSrc('/js/translator-engine.js');
      s.onload = function () { if (window.TranslatorEngine) resolve(window.TranslatorEngine); else reject(new Error('engine missing')); };
      s.onerror = function () { translatorP = null; reject(new Error('engine load')); };
      document.head.appendChild(s);
    });
    return translatorP;
  }
  function lineTranslation(li, line) {
    if (!mtOn || S.view === 'vocab') return null;
    var src = lineSource(line);
    if (!src || !lineHasWords(line)) return null;
    var got = mtCache.get(src);
    if (got === undefined && !mtPending && !mtError) return null;
    var d = el('div', 'rs-tr', got === undefined ? (mtError || '…') : got);
    d.lang = LOC; d.dir = 'auto';
    return d;
  }
  function translateLines() {
    var my = ++mtRun;
    var want = [];
    M.lines.forEach(function (line) {
      var src = lineSource(line);
      if (src && lineHasWords(line) && !mtCache.has(src) && want.indexOf(src) < 0) want.push(src);
    });
    if (!want.length) { mtPending = false; render(); return; }
    mtPending = true; mtError = ''; render();
    loadTranslator().then(function (engine) {
      var failed = 0, provider = '';
      return want.reduce(function (p, line) {
        return p.then(function () {
          if (my !== mtRun || !mtOn) return null;
          return engine.translateEntryText(line, 'en', LOC, null, mtOpts).then(function (r) {
            if (r && !r.reason && r.text) { mtCache.set(line, r.text); provider = r.provider || provider; }
            else failed++;
          }, function () { failed++; }).then(function () { if (my === mtRun) render(); });
        });
      }, Promise.resolve()).then(function () {
        if (my !== mtRun) return;
        mtPending = false;
        if (failed === want.length) mtError = t('mt_failed');
        render();
        feature(failed === want.length ? 'mt_failed' : 'mt_result', { provider: provider || null, lines: want.length, failed: failed }, true);
      });
    }).catch(function () {
      if (my !== mtRun) return;
      mtPending = false; mtError = t('mt_failed'); render();
    });
  }
  function toggleMt() {
    mtOn = !mtOn;
    E.mt.setAttribute('aria-pressed', String(mtOn));
    feature(mtOn ? 'mt_on' : 'mt_off');
    if (mtOn) translateLines(); else { mtRun++; mtPending = false; mtError = ''; render(); }
  }

  /* ── the word card ─────────────────────────────────────────────────────── */
  var HAS_POPOVER = typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';
  var HAS_ANCHOR = !!(window.CSS && CSS.supports && CSS.supports('position-anchor', '--rs-a'));
  var anchorEl = null;
  var openPops = [];
  function isSheet() { return !!(window.matchMedia && window.matchMedia('(max-width: 560px)').matches); }

  function showPop(pop, anchor) {
    if (anchorEl && anchorEl !== anchor) anchorEl.style.removeProperty('anchor-name');
    anchorEl = anchor;
    var anchored = HAS_ANCHOR && !isSheet();
    if (anchored) anchor.style.setProperty('anchor-name', '--rs-a');
    pop.classList.toggle('anchored', anchored);
    pop.classList.toggle('js-pos', !anchored && !isSheet());
    pop.classList.toggle('sheet', isSheet());
    if (HAS_POPOVER) {
      try { if (!pop.matches(':popover-open')) pop.showPopover(); } catch (e) { /* already open */ }
    } else {
      pop.hidden = false;
      pop.classList.add('open');
      if (openPops.indexOf(pop) < 0) openPops.push(pop);
    }
    if (!anchored && !isSheet()) placePop(pop, anchor);
  }
  function placePop(pop, anchor) {
    var r = anchor.getBoundingClientRect();
    var pw = pop.offsetWidth, ph = pop.offsetHeight;
    var vw = window.innerWidth, vh = window.innerHeight;
    var top = r.bottom + 8;
    if (top + ph > vh - 8 && r.top - 8 - ph > 8) top = r.top - 8 - ph;
    var left = Math.min(Math.max(8, r.left + r.width / 2 - pw / 2), Math.max(8, vw - pw - 8));
    pop.style.top = Math.round(top) + 'px';
    pop.style.left = Math.round(left) + 'px';
  }
  function hidePop(pop) {
    if (HAS_POPOVER) { try { if (pop.matches(':popover-open')) pop.hidePopover(); } catch (e) { /* ignore */ } }
    else { pop.hidden = true; pop.classList.remove('open'); openPops = openPops.filter(function (p) { return p !== pop; }); }
  }
  function closeCard() { if (E.card) hidePop(E.card); }

  function syllableLine(a) {
    var syl = (a && a.syllables) || [];
    if (syl.length < 2) return null;
    var box = el('span', 'rs-syl');
    box.lang = 'en';
    syl.forEach(function (s, i) {
      if (i) box.appendChild(el('span', 'rs-syl-dot', '·'));
      box.appendChild(i === a.stressIndex ? el('b', '', s) : document.createTextNode(s));
    });
    return box;
  }

  function cardRow(label, r, ipa, sayText, acc) {
    var row = el('div', 'rs-card-acc');
    row.appendChild(el('span', 'rs-card-lab', label));
    var rr = el('span', 'rs-card-rs');
    rr.lang = SCRIPT_LANG[S.script] || 'und'; rr.dir = SCRIPT_DIR[S.script] || 'ltr';
    if (r) paintRespelling(r, rr);
    row.appendChild(rr);
    if (ipa) { var ip = el('span', 'rs-card-ipa', '/' + ipa + '/'); ip.lang = 'en'; ip.dir = 'ltr'; row.appendChild(ip); }
    var b = el('button', 'rs-mini-play');
    b.type = 'button';
    b.setAttribute('data-say', sayText);
    b.setAttribute('data-acc', acc);
    b.setAttribute('aria-label', t('listen') + ' — ' + label);
    row.appendChild(b);
    return row;
  }

  function cardTips(r, box) {
    if (!r) return;
    var tips = CFG.tips || {}, seen = Object.create(null);
    var segs = r.segs || [];
    var ids = [];
    if (segs.length === 1 && !segs[0].u) ids = (r.sounds || []).map(function (id) { return [id, '']; });
    else segs.forEach(function (s) { if (s.approx && s.sound) ids.push([s.sound, s.o]); });
    ids.forEach(function (p) {
      var id = p[0];
      if (seen[id] || !tips[id]) return;
      seen[id] = 1;
      var d = el('div', 'rs-card-tip');
      var h = el('div', 'rs-card-tip-h');
      h.appendChild(el('b', '', tips[id].ipa));
      if (p[1]) h.appendChild(el('span', '', t('card_written', { x: p[1].replace(/\u0301/g, '') })));
      d.appendChild(h);
      d.appendChild(el('p', '', tips[id].text));
      box.appendChild(d);
    });
  }

  function openCard(target, tok) {
    if (!tok) return;
    var card = E.card, body = E.cardBody;
    body.textContent = '';
    var acc = accent(), other = acc === 'gb' ? 'us' : 'gb';
    var head = el('div', 'rs-card-head');
    if (tok.kind === 'native') {
      var hw = el('div', 'rs-card-word', tok.t);
      hw.lang = LOC;
      head.appendChild(hw);
      body.appendChild(head);
      var cands = tok.cands || [];
      if (!cands.length) {
        body.appendChild(el('p', 'rs-card-note', t('native_none', { w: tok.t })));
      } else {
        body.appendChild(el('div', 'rs-card-sub', t('native_title')));
        var list = el('div', 'rs-card-cands');
        cands.forEach(function (c) {
          var a = window.LangStress.analyzeWord(c, null, null, acc);
          var item = el('div', 'rs-card-cand' + (c === chosenEnglish(tok) ? ' on' : ''));
          var cw = el('span', 'rs-card-cw', c); cw.lang = 'en';
          item.appendChild(cw);
          var cr = el('span', 'rs-card-rs');
          cr.lang = SCRIPT_LANG[S.script] || 'und'; cr.dir = SCRIPT_DIR[S.script] || 'ltr';
          if (a && a.ipa) paintRespelling(respellIPA(a.ipa, a.ipaSyllables, null), cr);
          item.appendChild(cr);
          var pb = el('button', 'rs-mini-play');
          pb.type = 'button'; pb.setAttribute('data-say', c); pb.setAttribute('aria-label', t('listen') + ': ' + c);
          item.appendChild(pb);
          var use = el('button', 'tc-btn sm ghost', c === chosenEnglish(tok) ? t('native_chosen') : t('native_use'));
          use.type = 'button';
          use.setAttribute('data-pick', c);
          use.setAttribute('data-for', tok.t);
          item.appendChild(use);
          list.appendChild(item);
        });
        body.appendChild(list);
        var put = el('button', 'tc-btn sm', t('native_replace'));
        put.type = 'button';
        put.setAttribute('data-replace', tokRef(tok));
        body.appendChild(put);
      }
      showPop(card, target);
      feature('card_native');
      return;
    }
    var a = tok.a;
    var word = el('div', 'rs-card-word', tok.t);
    word.lang = 'en';
    head.appendChild(word);
    var syl = syllableLine(a);
    if (syl) head.appendChild(syl);
    body.appendChild(head);

    /* Both accents, the chosen one first — Cambridge shows both, and a learner
     * who hears «тома:тоу» on TV wants to know it is not a mistake. */
    var aOther = window.LangStress.analyzeWord(tok.t, tok.prev, tok.next, other);
    if (a.variants && tok.a.chosen != null && aOther.variants && aOther.variants[tok.a.chosen]) window.LangStress.pick(aOther, tok.a.chosen);
    var rHere = a.ipa ? respellIPA(a.ipa, a.ipaSyllables, null) : null;
    var rOther = aOther && aOther.ipa ? respellIPA(aOther.ipa, aOther.ipaSyllables, null, S.script, other) : null;
    var accs = el('div', 'rs-card-accs');
    accs.appendChild(cardRow(acc === 'us' ? 'US' : 'UK', rHere, a.ipa, tok.t, acc));
    if (aOther && aOther.ipa && aOther.ipa !== a.ipa) accs.appendChild(cardRow(other === 'us' ? 'US' : 'UK', rOther, aOther.ipa, tok.t, other));
    body.appendChild(accs);

    var acts = el('div', 'rs-card-acts');
    var b1 = el('button', 'tc-btn sm', t('listen')); b1.type = 'button'; b1.setAttribute('data-say', tok.t);
    var b2 = el('button', 'tc-btn sm ghost', t('slow')); b2.type = 'button'; b2.setAttribute('data-say', tok.t); b2.setAttribute('data-rate', '0.5');
    acts.appendChild(b1); acts.appendChild(b2);
    if (hdUsable()) {
      var b3 = el('button', 'tc-btn sm ghost rs-hd-say', t('listen_hd'));
      b3.type = 'button'; b3.setAttribute('data-say-hd', tok.t);
      acts.appendChild(b3);
    }
    body.appendChild(acts);

    if (tok.wf && tok.wf.weak && S.opt.weak) {
      var strong = respellIPA(tok.a.ipa, tok.a.ipaSyllables, null);
      body.appendChild(el('p', 'rs-card-note', t('card_weak', { weak: tok.r ? tok.r.text : '', strong: strong ? strong.text : '' })));
    }
    if (a.isHeteronym && a.variants && a.variants.length > 1) {
      var hb = el('div', 'rs-card-het');
      hb.appendChild(el('div', 'rs-card-sub', t('card_het')));
      a.variants.forEach(function (v, vi) {
        var vb = el('button', 'tc-chip' + (vi === a.chosen ? ' on' : ''));
        vb.type = 'button';
        vb.setAttribute('data-variant', vi);
        vb.setAttribute('data-k', tokRef(tok));
        var vr = respellIPA(v.ipa, v.ipaSyllables, null);
        vb.textContent = (vr ? vr.text : '') + '  /' + v.ipa + '/' + (v.role ? ' · ' + (t('role_' + v.role) || v.role) : '');
        hb.appendChild(vb);
      });
      body.appendChild(hb);
    }
    if (glossShown()) {
      var g = window.LangGloss.lookup(tok.t, (a.isHeteronym && a.role) ? a.role : window.LangGloss.posHint(tok.prev, tok.next));
      if (g) {
        var gl = el('div', 'rs-card-gloss');
        gl.appendChild(el('span', 'rs-card-sub', t('col_gloss')));
        var gt = el('span', '', window.LangGloss.full(g)); gt.lang = LOC; gt.dir = 'auto';
        gl.appendChild(gt);
        body.appendChild(gl);
      }
    }
    cardTips(tok.r, body);
    if (a.approximate) body.appendChild(el('p', 'rs-card-note', t('card_oov')));
    showPop(card, target);
    feature('card_open');
  }

  /* ── voices ────────────────────────────────────────────────────────────── */
  var HD = CFG.hd || null;
  var hdCache = new Map();
  var hdBlockedUntil = 0, hdBlockMsg = '';
  function hdUsable() { return !!(HD && HD.open) && Date.now() >= hdBlockedUntil; }
  function hdVoiceId(acc) { return (acc || accent()) === 'us' ? (HD.voices.us || 'EN-US') : (HD.voices.uk || 'EN-BR'); }
  function hdRate(speed) { return speed >= 1 ? 0 : Math.max(-50, Math.round((speed - 1) * 100)); }
  function csrf() { var m = document.querySelector('meta[name="csrf-token"]'); return m ? m.getAttribute('content') : ''; }
  function hdErr(kind, extra) { var e = new Error(kind); e.kind = kind; if (extra) for (var k in extra) e[k] = extra[k]; return e; }

  function hdFetch(text, rate, signal, acc) {
    var voice = hdVoiceId(acc);
    var key = voice + '|' + rate + '|' + text;
    if (hdCache.has(key)) return Promise.resolve(hdCache.get(key));
    if (Date.now() < hdBlockedUntil) return Promise.reject(hdErr('blocked'));
    return fetch('/api/tts/synthesize', {
      method: 'POST', credentials: 'same-origin', signal: signal, redirect: 'error',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json, audio/mpeg', 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ text: text, language: 'en', voice: voice, format: 'mp3', rate: rate, pitch: 0 })
    }).then(function (resp) {
      if (resp.status === 429) {
        return resp.json().catch(function () { return {}; }).then(function (b) {
          if (b.error === 'daily_quota') throw hdErr('day', { retryAfter: 0, paid: !!HD.isPro });
          throw hdErr(b.scope === 'day' ? 'day' : 'minute', { retryAfter: +b.retry_after || 60, cap: b.cap, paid: !!b.paid });
        });
      }
      if (resp.status === 503) throw hdErr('busy');
      if (!resp.ok) throw hdErr('refused', { status: resp.status });
      return resp.blob();
    }, function (e) {
      if (e && e.name === 'AbortError') throw e;
      throw hdErr('offline');
    }).then(function (blob) {
      if (!blob || !/^audio\//.test(blob.type || '') || blob.size < 200) throw hdErr('refused');
      var url = URL.createObjectURL(blob);
      hdCache.set(key, url);
      if (hdCache.size > 40) {
        var first = hdCache.keys().next().value;
        try { URL.revokeObjectURL(hdCache.get(first)); } catch (e) { /* ignore */ }
        hdCache.delete(first);
      }
      feature('hd_ok', { chars: text.length }, false);
      return url;
    });
  }

  /* A refusal of the server voice is never silence: the caller finishes on the
   * device voice, and the reason is said once, in the voice panel. */
  function hdFailed(err) {
    var kind = (err && err.kind) || 'refused';
    if (kind === 'day') {
      hdBlockedUntil = Date.now() + Math.max(60, err.retryAfter || 3600) * 1000;
      hdBlockMsg = err.paid ? t('hd_day_paid') : t('hd_day', { cap: err.cap || HD.free });
      setVoice('device', true);
    } else if (kind === 'minute') {
      hdBlockedUntil = Date.now() + Math.max(10, err.retryAfter || 60) * 1000;
      hdBlockMsg = t('hd_minute');
    } else if (kind !== 'blocked') {
      hdBlockMsg = t('hd_failed');
    }
    showVoiceMsg(hdBlockMsg, kind === 'day' && !err.paid);
    feature('hd_refused', { kind: kind }, true);
  }
  function showVoiceMsg(text, upsell) {
    var box = E.msg;
    box.textContent = '';
    if (!text) { box.hidden = true; return; }
    box.hidden = false;
    box.appendChild(document.createTextNode(text + ' '));
    if (upsell && HD && HD.url && !HD.isPro) {
      var a = el('a', 'tc-perk', t('hd_upsell', { plan: HD.plan, n: HD.pro }));
      a.href = HD.url;
      a.setAttribute('data-perk', 'hd_voice');
      box.appendChild(a);
    }
  }

  var P = { on: false, gen: 0, abort: null };
  function audioEl() {
    var g = window.__audioGate;
    if (g && g.element) return g.element();
    if (!P.audio) P.audio = new Audio();
    return P.audio;
  }
  function unlockAudio() {
    var g = window.__audioGate;
    if (g && g.unlockNow) { try { g.unlockNow(audioEl()); } catch (e) { /* ignore */ } }
  }
  function playUrl(url) {
    var a = audioEl(), g = window.__audioGate;
    if (g && g.play) return g.play(a, url);
    a.src = url;
    return a.play();
  }

  function sayWord(word, opts) {
    opts = opts || {};
    stopAll();
    if (opts.hd && hdUsable()) {
      unlockAudio();
      var btn = opts.btn;
      if (btn) btn.classList.add('busy');
      hdFetch(word, hdRate(opts.rate || 1), null, opts.acc).then(function (url) {
        if (btn) btn.classList.remove('busy');
        return playUrl(url);
      }).catch(function (err) {
        if (btn) btn.classList.remove('busy');
        hdFailed(err);
        deviceSay(word, opts);
      });
      feature('say_hd');
      return;
    }
    deviceSay(word, opts);
  }
  function deviceSay(word, opts) {
    if (!window.TTSHelper || !window.TTSHelper.isSupported()) { showVoiceMsg(t('no_voice')); return; }
    window.TTSHelper.speak(word, {
      lang: (opts.acc || accent()) === 'us' ? 'en-US' : 'en-GB',
      rate: opts.rate || 1,
      onerror: function () { showVoiceMsg(t('voice_failed')); },
      onstall: function () { showVoiceMsg(t('no_voice')); }
    });
  }

  function speakable() {
    var items = [];
    M.lines.forEach(function (line, li) {
      if (!lineHasWords(line)) return;
      var sp = Core.spoken(line, function (tok) {
        if (tok.kind === 'native') return chosenEnglish(tok);
        return tok.t;
      });
      if (sp.text.replace(/[^A-Za-z0-9]/g, '')) items.push({ li: li, text: sp.text, spans: sp.spans });
    });
    return items;
  }

  function markLines(lis) {
    var set = Object.create(null);
    (lis || []).forEach(function (l) { set[l] = 1; });
    var nodes = E.out.querySelectorAll('[data-li]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].classList.contains('rs-row-play')) continue;
      nodes[i].classList.toggle('now', !!set[nodes[i].getAttribute('data-li')]);
    }
  }
  var nowWord = null;
  function markWord(li, ti) {
    if (nowWord) { nowWord.classList.remove('now'); nowWord = null; }
    if (li == null || li < 0 || ti < 0) return;
    var node = E.out.querySelector('[data-k="' + li + ':' + ti + '"]');
    if (!node) return;
    node.classList.add('now');
    nowWord = node;
    keepVisible(node);
  }
  /* The output scrolls inside its own box on a wide screen; the word being
   * read is kept inside that box by moving ITS scrollTop only — never the page. */
  function keepVisible(node) {
    var box = E.out;
    if (box.scrollHeight <= box.clientHeight + 2) return;
    var nb = node.getBoundingClientRect(), bb = box.getBoundingClientRect();
    if (nb.top < bb.top + 8) box.scrollTop -= (bb.top + 8 - nb.top);
    else if (nb.bottom > bb.bottom - 8) box.scrollTop += (nb.bottom - bb.bottom + 8);
  }

  function setPlayBtn(state) {
    var b = E.play;
    b.classList.toggle('on', state === 'on' || state === 'loading');
    b.classList.toggle('loading', state === 'loading');
    b.setAttribute('aria-pressed', String(!!state));
    b.querySelector('span').textContent = state ? t('stop') : t('play_all');
  }

  function stopAll() {
    P.gen++;
    if (P.abort) { try { P.abort.abort(); } catch (e) { /* ignore */ } P.abort = null; }
    if (P.on && window.TTSHelper) { try { window.TTSHelper.cancel(); } catch (e) { /* ignore */ } }
    var a = P.audioUsed ? audioEl() : null;
    if (a) { try { a.pause(); } catch (e) { /* ignore */ } a.onended = null; }
    P.on = false; P.audioUsed = false;
    if (E.play) setPlayBtn(false);
    if (E.out) { markLines([]); markWord(-1); }
  }

  function playAll(fromLi) {
    if (P.on) { stopAll(); return; }
    var items = speakable();
    if (fromLi != null) items = items.filter(function (it) { return it.li === fromLi; });
    /* Play is enabled by words on screen, but a Russian word with no English
     * match has nothing to say aloud — the press used to end here in silence. */
    if (!items.length) { toast(t('no_english'), 'info'); feature('play_nothing'); return; }
    feature('play', { voice: S.voice, speed: S.speed, lines: items.length });
    if (S.voice === 'hd' && hdUsable()) { unlockAudio(); playHD(items); return; }
    playDevice(items, 0);
  }

  function playDevice(items, from) {
    if (!window.TTSHelper || !window.TTSHelper.isSupported()) { showVoiceMsg(t('no_voice')); return; }
    var my = ++P.gen;
    P.on = true;
    setPlayBtn('on');
    (function next(i) {
      if (my !== P.gen) return;
      if (i >= items.length) { stopAll(); return; }
      var it = items[i];
      markLines([it.li]);
      markWord(-1);
      window.TTSHelper.speak(it.text, {
        lang: ttsLang(), rate: S.speed,
        onboundary: function (e) {
          if (my !== P.gen || !e || typeof e.charIndex !== 'number') return;
          markWord(it.li, Core.spanAt(it.spans, e.charIndex));
        },
        onend: function () { if (my === P.gen) next(i + 1); },
        onerror: function () { if (my !== P.gen) return; stopAll(); showVoiceMsg(t('voice_failed')); },
        onstall: function () { if (my !== P.gen) return; stopAll(); showVoiceMsg(t('no_voice')); }
      });
    })(from || 0);
  }

  function playHD(items) {
    var my = ++P.gen;
    P.on = true; P.audioUsed = true;
    setPlayBtn('loading');
    var chunks = Core.chunkLines(items.map(function (x) { return x.text; }), HD_FIRST, HD_MAX).map(function (idx) {
      return { items: idx.map(function (k) { return items[k]; }), text: idx.map(function (k) { return items[k].text; }).join('\n') };
    });
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    P.abort = ctrl;
    var urls = [];
    var rate = hdRate(S.speed);
    function get(k) { if (!urls[k]) urls[k] = hdFetch(chunks[k].text, rate, ctrl && ctrl.signal); return urls[k]; }
    feature('play_hd');
    (function next(k) {
      if (my !== P.gen) return;
      if (k >= chunks.length) { stopAll(); return; }
      get(k).then(function (url) {
        if (my !== P.gen) return;
        if (k + 1 < chunks.length) get(k + 1).catch(noop);
        markLines(chunks[k].items.map(function (x) { return x.li; }));
        setPlayBtn('on');
        var a = audioEl();
        a.onended = function () { if (my === P.gen) next(k + 1); };
        Promise.resolve(playUrl(url)).catch(function () {
          if (my !== P.gen) return;
          hdFailed(hdErr('refused'));
          playDevice(items, items.indexOf(chunks[k].items[0]));
        });
      }).catch(function (err) {
        if (my !== P.gen || (err && err.name === 'AbortError')) return;
        hdFailed(err);
        P.audioUsed = false;
        playDevice(items, items.indexOf(chunks[k].items[0]));
      });
    })(0);
  }

  function setVoice(v, quiet) {
    if (v === 'hd' && !(HD && HD.open)) v = 'device';
    S.voice = v;
    if (E.voice) {
      E.voice.classList.toggle('on', v === 'hd');
      E.voice.setAttribute('aria-pressed', String(v === 'hd'));
    }
    if (E.hdNote) E.hdNote.hidden = v !== 'hd';
    if (!quiet) { ssave('voice', v); feature('voice_' + v); }
  }
  function setSpeed(sp) {
    if (SPEEDS.indexOf(sp) < 0) sp = 1;
    S.speed = sp;
    E.speed.querySelectorAll('[data-speed]').forEach(function (b) {
      var on = +b.getAttribute('data-speed') === sp;
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', String(on));
    });
  }

  /* ── copy · share · print ─────────────────────────────────────────────── */
  function plainRespelling() {
    return Core.plainText(M.lines, function (tok) {
      if (tok.kind === 'native') return tok.r ? tok.r.text : tok.t;
      return tok.r ? tok.r.text : tok.t;
    });
  }
  function copyText(text, btn, label) {
    var done = function () {
      if (!btn) return;
      btn.setAttribute('data-flash', '1');
      var sp = btn.querySelector('span');
      sp.textContent = t('copied');
      setTimeout(function () { btn.removeAttribute('data-flash'); sp.textContent = label; }, 1400);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, function () { legacyCopy(text); done(); });
    else { legacyCopy(text); done(); }
  }
  function legacyCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (e) { /* nothing more to try */ }
    document.body.removeChild(ta);
  }
  function doCopy() {
    if (S.view === 'vocab') { copyText(lastTable, E.copy, t('copy_table')); feature('copy_table'); return; }
    var text = plainRespelling();
    if (S.view === 'lines' && S.opt.en) {
      var rows = [];
      M.lines.forEach(function (line) {
        if (!lineHasContent(line)) { rows.push(''); return; }
        var r = Core.plainText([line], function (tok) { return tok.r ? tok.r.text : tok.t; });
        rows.push(lineSource(line));
        rows.push(r);
        var tr = mtOn ? mtCache.get(lineSource(line)) : null;
        if (tr) rows.push(tr);
        rows.push('');
      });
      text = rows.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    }
    copyText(text, E.copy, t('copy'));
    feature('copy', { view: S.view });
  }
  function doShare() {
    var hash = Core.encodeShare({ text: E.input.value.slice(0, 4000), script: S.script, accent: accent() === 'us' ? 'us' : 'uk', view: S.view });
    var url = location.origin + location.pathname + hash;
    feature('share');
    var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (coarse && navigator.share) {
      navigator.share({ title: document.title, url: url }).catch(noop);
      return;
    }
    copyText(url, E.share, t('share'));
    toast(t('share_copied'), 'success');
  }

  /* ── photo → text ─────────────────────────────────────────────────────── */
  /* The photo lane is the shared one (public/js/lang/photo-text.js, the panel
   * tools/partials/photo-text): reading order across columns, paragraphs
   * rebuilt, the reader's own language and its Latin look-alikes left out,
   * a frame per paragraph to take one out or put it back. A picture from the
   * clipboard button goes the same way as a picked photo. */
  function ocrPasted(file, via) {
    var lane = window.PhotoText && window.PhotoText.of('rsPhoto');
    if (lane) lane.take(file, via);
  }

  /* «Вставить» found no text. It used to stop right there, and the press did
   * nothing on screen (24 sessions in 8 days). A screenshot in the clipboard
   * has no text but is exactly what the photo reader is for — Ctrl+V already
   * sent it there; the button now does too. Otherwise say the clipboard is
   * empty instead of staying silent. */
  function pasteImageOrSayEmpty() {
    function empty() { E.input.focus(); toast(t('paste_empty'), 'info'); feature('paste_empty'); }
    if (!navigator.clipboard.read) { empty(); return; }
    return navigator.clipboard.read().then(function (items) {
      for (var i = 0; i < items.length; i++) {
        var type = (items[i].types || []).filter(function (x) { return /^image\//.test(x); })[0];
        if (type) {
          return items[i].getType(type).then(function (blob) {
            ocrPasted(new File([blob], 'clipboard.' + type.split('/')[1], { type: type }), 'ocr_paste_btn');
          });
        }
      }
      empty();
    }).catch(empty);
  }

  /* ── input ────────────────────────────────────────────────────────────── */
  var FIELD_SIZING = !!(window.CSS && CSS.supports && CSS.supports('field-sizing', 'content'));
  function autosize() {
    if (FIELD_SIZING) return;
    var ta = E.input;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight + 2, Math.round(window.innerHeight * 0.55)) + 'px';
  }
  var draftTimer = 0;
  function onInput() {
    userEdited = true;
    offerResult = ''; offerBusy = false;
    autosize();
    paintCount();
    schedule();
    clearTimeout(draftTimer);
    draftTimer = setTimeout(function () { ssave('draft', E.input.value.slice(0, 5000)); }, 700);
  }

  /* ── events ───────────────────────────────────────────────────────────── */
  function onOutClick(e) {
    var play = e.target.closest('.rs-row-play');
    if (play) { stopAll(); playAll(+play.getAttribute('data-li')); feature('play_line'); return; }
    var mini = e.target.closest('[data-say]');
    if (mini) { sayWord(mini.getAttribute('data-say'), {}); feature('say_vocab'); return; }
    var ap = e.target.closest('.v-r .ap[data-s]');
    if (ap) { openTip(ap, ap.getAttribute('data-s')); return; }
    var w = e.target.closest('.rs-w, .rs-lw, .v-w');
    if (!w) return;
    var host = w.classList.contains('v-w') ? w.closest('[data-k]') : w;
    var tok = host ? tokOf(host.getAttribute('data-k')) : null;
    if (!tok || tok.kind === 'num') return;
    var sayText = tok.kind === 'native' ? chosenEnglish(tok) : tok.t;
    if (sayText) sayWord(sayText, {});
    openCard(w, tok);
  }
  function onOutKey(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var w = e.target.closest('.rs-w, .v-w');
    if (!w) return;
    e.preventDefault();
    var host = w.classList.contains('v-w') ? w.closest('[data-k]') : w;
    var tok = tokOf(host.getAttribute('data-k'));
    if (!tok) return;
    var sayText = tok.kind === 'native' ? chosenEnglish(tok) : tok.t;
    if (sayText) sayWord(sayText, {});
    openCard(w, tok);
  }
  function onCardClick(e) {
    var hd = e.target.closest('[data-say-hd]');
    if (hd) { sayWord(hd.getAttribute('data-say-hd'), { hd: true, btn: hd }); return; }
    var say = e.target.closest('[data-say]');
    if (say) {
      sayWord(say.getAttribute('data-say'), { rate: +(say.getAttribute('data-rate') || 1), acc: say.getAttribute('data-acc') || null });
      feature(say.getAttribute('data-rate') ? 'card_slow' : 'card_listen');
      return;
    }
    var v = e.target.closest('[data-variant]');
    if (v) {
      var tok = tokOf(v.getAttribute('data-k'));
      if (!tok) return;
      picks[tok.key] = +v.getAttribute('data-variant');
      feature('pick_variant');
      var ref = tokRef(tok);
      reanalyseAll();
      var node = E.out.querySelector('[data-k="' + ref + '"]');
      if (node) openCard(node, tokOf(ref));
      return;
    }
    var pk = e.target.closest('[data-pick]');
    if (pk) {
      nativePick[nativeKey(pk.getAttribute('data-for'))] = pk.getAttribute('data-pick');
      feature('native_pick');
      var nodeRef = anchorEl ? anchorEl.getAttribute('data-k') : null;
      reanalyseAll();
      var n2 = nodeRef ? E.out.querySelector('[data-k="' + nodeRef + '"]') : null;
      if (n2) openCard(n2, tokOf(nodeRef));
      return;
    }
    var rep = e.target.closest('[data-replace]');
    if (rep) { replaceNative(tokOf(rep.getAttribute('data-replace'))); return; }
    if (e.target.closest('.rs-pop-x')) closeCard();
  }
  /* «Подставить в текст»: the word typed in the reader's language becomes the
   * English one in the field, at the same place. */
  function replaceNative(tok) {
    if (!tok) return;
    var en = chosenEnglish(tok);
    if (!en) return;
    var lines = E.input.value.split('\n');
    var line = lines[tok.li];
    if (line == null) return;
    var idx = -1, n = 0, re = new RegExp('(^|[^\\p{L}])(' + tok.t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')(?![\\p{L}])', 'gu');
    var count = 0;
    for (var i = 0; i < tok.ti; i++) if (M.lines[tok.li][i].t === tok.t) count++;
    line.replace(re, function (m, pre, w, off) { if (n === count && idx < 0) idx = off + pre.length; n++; return m; });
    if (idx < 0) return;
    lines[tok.li] = line.slice(0, idx) + en + line.slice(idx + tok.t.length);
    closeCard();
    replaceField(lines.join('\n'));
    feature('native_replace');
  }

  function openSettings() {
    showPop(E.settings, E.gear);
    feature('settings_open');
  }
  function onOpt(e) {
    var cb = e.target.closest('input[data-opt]');
    if (!cb) return;
    var k = cb.getAttribute('data-opt');
    S.opt[k] = cb.checked;
    ssave('opt_' + k, cb.checked ? 1 : 0);
    feature('opt_' + k + (cb.checked ? '_on' : '_off'));
    if (k === 'gloss') {
      if (cb.checked) glossReady().then(function (ok) {
        var words = [];
        M.lines.forEach(function (l) { l.forEach(function (x) { if (x.kind === 'en') words.push(x.t); }); });
        return ok && words.length ? window.LangGloss.ensure(uniq(words)).catch(noop) : null;
      }).then(render);
      else render();
    } else if (k === 'en') render();
    else respellAll();
  }

  function optRow(k) {
    var cb = E.settings.querySelector('input[data-opt="' + k + '"]');
    return cb ? cb.closest('.rs-opt') : { hidden: false };
  }
  function setView(v, fromUser) {
    if (['over', 'lines', 'vocab'].indexOf(v) < 0) v = 'over';
    var apply = function () {
      S.view = v;
      E.view.querySelectorAll('[data-view]').forEach(function (b) {
        var on = b.getAttribute('data-view') === v;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', String(on));
      });
      E.root.setAttribute('data-view', v);
      render();
    };
    if (fromUser && window.__viewTransition) window.__viewTransition(apply); else apply();
    if (fromUser) { ssave('view', v); feature('view_' + v); }
  }
  function setScript(code, fromUser) {
    var ok = (CFG.scripts || []).some(function (s) { return s.c === code; });
    if (!ok) code = CFG.script || 'ru';
    S.script = code;
    E.script.value = code;
    E.root.setAttribute('data-script', code);
    /* a switch the chosen letters cannot honour is not offered: Hangul and
     * Arabic mark no stress, five of the alphabets write no length */
    var LS = window.LangScript;
    optRow('stress').hidden = !!(LS && LS.stressStyleOf && LS.stressStyleOf(code) === 'none');
    optRow('long').hidden = !!(LS && LS.lengthStyleOf && LS.lengthStyleOf(code) === 'none');
    if (M.lines.length) respellAll();
    if (fromUser) { ssave('script', code); feature('script_' + code); }
  }

  /* ── boot ─────────────────────────────────────────────────────────────── */
  function initialText() {
    var sh = Core.decodeShare(location.hash);
    if (sh && sh.text) {
      try { history.replaceState(null, '', location.pathname + location.search); } catch (e) { /* ignore */ }
      feature('share_open');
      return sh;
    }
    var draft = sload('draft', null);
    if (typeof draft === 'string') return { text: draft };
    return { text: CFG.demo || '' };
  }

  function boot() {
    if (boot.done) return;
    boot.done = true;
    E.root = $('rsTool');
    if (!E.root) return;
    ['input', 'out', 'script', 'view', 'gear', 'settings', 'play', 'speed', 'voice', 'hdNote', 'copy', 'share', 'print', 'mt',
      'legend', 'msg', 'homo', 'offer', 'offerGo', 'offerUse', 'card', 'cardBody',
      'examples', 'clear', 'paste', 'count'].forEach(function (k) {
      E[k] = $('rs' + k.charAt(0).toUpperCase() + k.slice(1));
    });
    if (!Core) { E.out.textContent = t('engine_error'); return; }
    /* The card and the settings ship `hidden` so a browser without the Popover
     * API never shows them in the page flow; where the API exists, `popover`
     * itself keeps them out of sight until opened. */
    if (HAS_POPOVER) { E.card.hidden = false; E.settings.hidden = false; }

    var start = initialText();
    // settings the person made last time (a shared link brings its own)
    S.opt.stress = !!+sload('opt_stress', 1);
    S.opt.long = !!+sload('opt_long', 1);
    S.opt.weak = !!+sload('opt_weak', 1);
    S.opt.gloss = CFG.gloss ? !!+sload('opt_gloss', 1) : false;
    S.opt.en = !!+sload('opt_en', 1);
    E.settings.querySelectorAll('input[data-opt]').forEach(function (cb) { cb.checked = !!S.opt[cb.getAttribute('data-opt')]; });
    setSpeed(+sload('speed', 1));
    setVoice(sload('voice', 'device'), true);
    setScript(start.script || sload('script', CFG.script || 'ru'), false);
    setView(start.view || sload('view', 'over'), false);
    if (start.accent && window.__enVariant) window.__enVariant.set(start.accent);

    E.input.value = start.text || '';
    autosize();
    paintCount();

    E.input.addEventListener('input', onInput);
    E.out.addEventListener('click', onOutClick);
    E.out.addEventListener('keydown', onOutKey);
    E.card.addEventListener('click', onCardClick);
    E.legend.addEventListener('click', function (e) {
      var b = e.target.closest('[data-s]');
      if (!b) return;
      openTip(b, b.getAttribute('data-s'));
    });
    E.script.addEventListener('change', function () { setScript(E.script.value, true); });
    E.view.addEventListener('click', function (e) { var b = e.target.closest('[data-view]'); if (b) setView(b.getAttribute('data-view'), true); });
    E.speed.addEventListener('click', function (e) {
      var b = e.target.closest('[data-speed]'); if (!b) return;
      setSpeed(+b.getAttribute('data-speed')); ssave('speed', S.speed); feature('speed_' + S.speed);
      if (P.on) { stopAll(); playAll(); }
    });
    if (E.voice) E.voice.addEventListener('click', function () {
      var want = S.voice === 'hd' ? 'device' : 'hd';
      if (want === 'hd' && !hdUsable()) { showVoiceMsg(hdBlockMsg || t('hd_failed'), !HD.isPro); return; }
      setVoice(want);
      if (P.on) { stopAll(); playAll(); }
    });
    E.play.addEventListener('click', function () { playAll(); });
    E.copy.addEventListener('click', doCopy);
    E.share.addEventListener('click', doShare);
    E.print.addEventListener('click', function () { feature('print'); closeCard(); window.print(); });
    if (E.mt) {
      if (CFG.gloss) E.mt.hidden = false;
      E.mt.addEventListener('click', toggleMt);
    }
    E.gear.addEventListener('click', function () {
      if (HAS_POPOVER && E.settings.matches(':popover-open')) { hidePop(E.settings); return; }
      openSettings();
    });
    E.settings.addEventListener('change', onOpt);
    E.homo.querySelector('button').addEventListener('click', fixHomoglyphsInField);
    E.offerGo.addEventListener('click', offerTranslate);
    E.offerUse.addEventListener('click', offerApply);
    E.examples.addEventListener('click', function (e) {
      var b = e.target.closest('[data-ex]');
      if (!b) return;
      E.input.value = b.getAttribute('data-ex');
      onInput();
      feature('example', { ex: b.getAttribute('data-ex').slice(0, 24) });
    });
    E.clear.addEventListener('click', function () { replaceField(''); E.input.focus(); feature('clear'); });
    if (E.paste) {
      if (navigator.clipboard && navigator.clipboard.readText) {
        E.paste.hidden = false;
        E.paste.addEventListener('click', function () {
          navigator.clipboard.readText().then(function (txt) {
            if (!txt || !txt.trim()) return pasteImageOrSayEmpty();
            var cur = E.input.value.trim();
            replaceField(!cur || cur === String(CFG.demo || '').trim() ? txt : E.input.value.replace(/\s+$/, '') + '\n' + txt);
            feature('paste_btn');
          }).catch(function () { E.input.focus(); toast(t('paste_blocked'), 'info'); });
        });
      }
    }
    if (HAS_POPOVER) {
      E.card.addEventListener('toggle', function (e) {
        if (e.newState === 'closed' && anchorEl) { anchorEl.style.removeProperty('anchor-name'); }
      });
    } else {
      document.addEventListener('click', function (e) {
        openPops.slice().forEach(function (p) {
          if (!p.contains(e.target) && (!anchorEl || !anchorEl.contains(e.target)) && e.target !== E.gear) hidePop(p);
        });
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') openPops.slice().forEach(hidePop); });
    }
    window.addEventListener('resize', function () {
      if (!HAS_ANCHOR && anchorEl && E.card.classList.contains('js-pos')) placePop(E.card, anchorEl);
    });
    window.addEventListener('scroll', function () {
      if (anchorEl && (E.card.classList.contains('js-pos') || E.settings.classList.contains('js-pos'))) { hidePop(E.card); hidePop(E.settings); }
    }, { passive: true });
    if (window.__enVariant) window.__enVariant.onChange(function () {
      feature('accent_' + accent());
      if (M.lines.length) reanalyseAll();
    });
    document.addEventListener('visibilitychange', function () { if (document.hidden && P.on && S.voice === 'device') stopAll(); });

    schedule(0);
  }

  function openTip(anchor, id) {
    var tip = (CFG.tips || {})[id];
    if (!tip) return;
    var body = E.cardBody;
    body.textContent = '';
    var head = el('div', 'rs-card-head');
    head.appendChild(el('div', 'rs-card-word', tip.ipa));
    var ex = el('span', 'rs-syl', tip.ex); ex.lang = 'en';
    head.appendChild(ex);
    body.appendChild(head);
    body.appendChild(el('p', 'rs-card-tiptext', tip.text));
    var acts = el('div', 'rs-card-acts');
    var b = el('button', 'tc-btn sm', t('listen') + ': ' + tip.ex);
    b.type = 'button'; b.setAttribute('data-say', tip.ex);
    acts.appendChild(b);
    var b2 = el('button', 'tc-btn sm ghost', t('slow')); b2.type = 'button'; b2.setAttribute('data-say', tip.ex); b2.setAttribute('data-rate', '0.5');
    acts.appendChild(b2);
    body.appendChild(acts);
    showPop(E.card, anchor);
    feature('tip_' + id);
  }

  if (document.readyState === 'complete') boot();
  else {
    document.addEventListener('DOMContentLoaded', boot);
    window.addEventListener('load', boot);
  }
})();
